import { useState, useEffect, useCallback } from 'react';
import { HistoryEntry, Outcome } from '../types';
import { supabase } from '../utils/supabase';

export const useHistory = (playerId: string | null) => {
  const [history, setHistory] = useState<HistoryEntry[]>([]);
  const [loading, setLoading] = useState(false);

  const fetchHistory = useCallback(async () => {
    if (!playerId) return;

    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('matches')
        .select('*')
        .or(`player1_id.eq.${playerId},player2_id.eq.${playerId}`)
        .order('created_at', { ascending: false })
        .limit(30);

      if (error) {
        console.error('[useHistory] Failed to fetch history:', error.message);
        return;
      }

      const entries: HistoryEntry[] = (data || []).map((match: any) => {
        const isPlayer1 = match.player1_id === playerId;
        const myScore = isPlayer1 ? match.player1_score : match.player2_score;
        const theirScore = isPlayer1 ? match.player2_score : match.player1_score;
        const opponentName = isPlayer1 ? (match.player2_name || 'Unknown') : (match.player1_name || 'Unknown');

        let outcome: Outcome;
        if (match.winner === null) {
          outcome = 'TIE';
        } else if (match.winner === playerId) {
          outcome = 'VICTORY';
        } else {
          outcome = 'DEFEAT';
        }

        const date = new Date(match.created_at);
        const dateStr = `${date.getMonth() + 1}/${date.getDate()}/${date.getFullYear()}`;

        return {
          id: match.id,
          playerScore: myScore,
          opponentScore: theirScore,
          opponentName,
          outcome,
          difficulty: match.difficulty || 'pvp',
          date: dateStr,
        };
      });

      setHistory(entries);
    } catch (err) {
      console.error('[useHistory] Unexpected error:', err);
    } finally {
      setLoading(false);
    }
  }, [playerId]);

  useEffect(() => {
    fetchHistory();
  }, [fetchHistory]);

  return { history, loading, refreshHistory: fetchHistory };
};
